import { Flex, InlineStack, Badge, Spinner, Text, Tooltip } from '@customerio/pluma-components/react';
import type { ComputedStatus, StatusDetails } from '@/features/pipelines/utils';

type StatusLabelColor = 'blue' | 'green' | 'gray' | 'violet' | 'red' | 'yellow';

interface StatusColorType {
  color: StatusLabelColor;
  label: string;
}

export const STATUS_OBJECTS: Record<ComputedStatus, StatusColorType> = {
  enabled: {
    color: 'green',
    label: 'Enabled',
  },
  disabled: {
    color: 'red',
    label: 'Disabled',
  },
  no_recent_data: {
    color: 'yellow',
    label: 'No recent data',
  },
  checking: {
    color: 'gray',
    label: 'Checking status',
  },
  failed: {
    color: 'gray',
    label: 'Pending',
  },
  disconnected: {
    color: 'gray',
    label: 'Disconnected',
  },
  needs_attention: {
    color: 'yellow',
    label: 'Needs attention',
  },
  oauth_disconnected: {
    color: 'yellow',
    label: 'Needs attention',
  },
};

type StatusDisplayProps = {
  isLoading: boolean;
  status?: StatusDetails;
  tooltip: (status: ComputedStatus, lastSeen?: number) => string;
};

export const StatusDisplay = ({ isLoading, status, tooltip }: StatusDisplayProps) => {
  const currentStatus = status ?? (isLoading ? { status: 'checking' } : { status: 'failed' });
  const statusTheming = STATUS_OBJECTS[currentStatus.status];
  return (
    <Flex gap="075" alignItems="center">
      {isLoading ? (
        <Spinner />
      ) : (
        <Tooltip content={tooltip(currentStatus.status, currentStatus.lastSeen)} placement="bottom">
          <Badge color={statusTheming.color}>{statusTheming.label}</Badge>
        </Tooltip>
      )}
    </Flex>
  );
};

type StatusBadgeProps = { status: ComputedStatus; plainLabel?: boolean };

export const StatusBadge = ({ status, plainLabel }: StatusBadgeProps) => {
  const isChecking = status === 'checking';
  const statusTheming = STATUS_OBJECTS[status];
  return isChecking ? (
    <InlineStack alignBlock="center" gap="200">
      <Spinner />
      <Text size="xs">checking…</Text>
    </InlineStack>
  ) : (
    <Badge color={statusTheming.color} showStatusIndicator={plainLabel !== true}>
      {statusTheming.label}
    </Badge>
  );
};
